import {
  Card,
  Box,
  CardBody,
  Text,
  Stack,
  Skeleton,
  Image,
} from '@chakra-ui/react';
import { NestedProject } from '@habity/types';
import { getProjectPath, getImageUrl } from '@habity/services';
import Link from 'next/link';
import { formatCurrency } from '@habity/utils';

type ProjectMapCardProps = {
  isLoading: boolean;
  project: NestedProject | undefined;
};

export const ProjectMapCard = ({ project, isLoading }: ProjectMapCardProps) => {
  if (isLoading) {
    return (
      <Card
        maxW="md"
        w="300px"
        overflow="hidden"
        m={{ mobile: '0', desktop: '4' }}
        borderRadius="4px"
        position="absolute"
        top="4"
        left="4"
        zIndex={10}
      >
        <Skeleton h="200px" w="100%" />
        <CardBody px="3" py="2">
          <Stack spacing={2}>
            <Skeleton h="20px" w="80%" />
            <Skeleton h="16px" w="60%" />
            <Skeleton h="16px" w="40%" />
          </Stack>
        </CardBody>
      </Card>
    );
  }

  if (!project) return null;

  const firstImage = project.images?.[0];
  const imageUrl = firstImage ? getImageUrl(firstImage, 'list') : null;
  const priceRange =
    project.price_min && project.price_max
      ? `${formatCurrency(project.price_min)} - ${formatCurrency(
        project.price_max
      )}`
      : project.price_min
        ? `Från ${formatCurrency(project.price_min)}`
        : project.price_max
          ? `Upp till ${formatCurrency(project.price_max)}`
          : null;

  return (
    <Card
      maxW="md"
      w="300px"
      overflow="hidden"
      m={{ mobile: '0', desktop: '4' }}
      borderRadius="4px"
      className="group"
      position="absolute"
      top="4"
      left="4"
      zIndex={10}
    >
      <Link href={getProjectPath(project)}>
        <Box position="relative" overflow="hidden" h="200px">
          {imageUrl ? (
            <Image
              src={imageUrl}
              alt={project.name || 'Project image'}
              objectFit="cover"
              w="100%"
              h="100%"
            />
          ) : (
            <Box bg="gray.200" h="100%" w="100%" />
          )}
        </Box>
        <CardBody px="3" py="2">
          <Stack spacing={1}>
            <Text fontWeight="semibold" fontSize="md" noOfLines={1}>
              {project.name}
            </Text>
            {project.city && (
              <Text fontSize="sm" color="gray.600">
                {project.city}
              </Text>
            )}
            {priceRange && (
              <Text fontSize="sm" fontWeight="medium">
                {priceRange}
              </Text>
            )}
            {project.total_homes && (
              <Text fontSize="xs" color="gray.500">
                {project.total_homes} bostäder
              </Text>
            )}
          </Stack>
        </CardBody>
      </Link>
    </Card>
  );
};
